import { useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Box, Theme, Typography } from '@mui/material';

import FirmBankingSystemAccountContainer from './FirmBankingSystemAccountContainer';
import FirmUnrestrictedAccountsView from './unrestrictedAccountsView/FirmUnrestrictedAccountsView';
import FirmTrustAccountsView from './trustAccountsView/FirmTrustAccountsView';
import { AccountTypeShortCuts, FirmBankingData, FirmBankingWithdrawFormSubmitProps } from './types';

import { getFirmBankingInfo, withdrawFirmFunds } from '../../api/firmsApi/firmsApi';
import PaginationProvider from '../../providers/PaginationProvider';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { firmWithdrawFundsFormSchema } from '../../helpers/formSchemas/formSchemas';
import { FIRM_WITHDRAW_FUNDS_FORM_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import VerificationPopup from '../../components/VerificationPopup';
import FirmBankingWithdrawPopup from './withdrawFundsForm/FirmBankingWithdrawPopup';
import FirmBankingWithdrawForm from './withdrawFundsForm/FirmBankingWithdrawForm';
import { SelectOption } from '../../types';
import useSnackBar from '../../hooks/useSnackBar';

const useStyles = makeStyles()((theme: Theme) => ({
  systemAccountsWrapper: {
    display: 'flex',
    marginTop: '16px',
    gap: '20px',
  },
  systemAccountContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  systemAccountsBlockLabel: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '120%',
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  systemAccountsLabel: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  systemAccountsInnerContainer: {
    display: 'flex',
    height: '100%',
    gap: '20px',

    '@media (max-width: 1300px)': {
      flexWrap: 'wrap',
      maxWidth: '500px',
    },
  },
}));

export const FirmBankingAccountsView = () => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();

  const firmId = localStorage.getItem('firmId');

  const [bankingData, setBankingData] = useState<FirmBankingData>();
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [isWithdrawalPopupOpen, setIsWithdrawalPopupOpen] = useState<boolean>(false);
  // const [isVerificationPopupOpen, setIsVerificationPopupOpen] = useState<boolean>(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [shortUnrestrictedAccountData, setShortUnrestrictedAccountData] = useState<SelectOption[]>(
    [],
  );
  const [withdrawFundsData, setWithdrawFundsData] = useState<{
    toUnrestrictedAccountId: number;
    amount: number;
    fromSystemAccountType: 'REV' | 'EXP' | 'LIEN';
  } | null>(null);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(firmWithdrawFundsFormSchema),
    defaultValues: FIRM_WITHDRAW_FUNDS_FORM_DEFAULT_VALUES,
  });

  const { handleSubmit } = formMethods;

  const onSubmitForm = (values: FirmBankingWithdrawFormSubmitProps) => {
    setIsFormSubmitting(true);
    console.log('submit data', values);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const submitData = {
      fromSystemAccountType:
        AccountTypeShortCuts[values.fromSystemAccountType as keyof typeof AccountTypeShortCuts],
      toUnrestrictedAccountId: Number(values.toUnrestrictedAccountId),
      amount: Number(values.amount),
    };
     setWithdrawFundsData(submitData);
  };

  const handleSubmitWithdraw = (pin: string) => {
    if (!withdrawFundsData) {
      return;
    }

    setIsFormSubmitting(true);

    withdrawFirmFunds(pin, withdrawFundsData)
      .then((res) => {
        if (res.status === 202) {
          setAlert((prev) => ({
            ...prev,
            message: 'Withdraw has been operated successfully.',
            type: 'success',
          }));

          setIsWithdrawalPopupOpen(false);
          fetchBankingData();
        }
      })
      .catch((err) => {
        setAlert((prev) => ({
          ...prev,
          message: err.response.data.message || 'Something went wrong...',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });

    setWithdrawFundsData(null);
  };

  // const handleCloseVerificationPopup = () => setIsVerificationPopupOpen(false);

  const fetchBankingData = () => {
    getFirmBankingInfo(firmId as string).then((res) => {
      setBankingData(res.data);
    });
  };

  useEffect(() => {
    fetchBankingData();
  }, []);

  return (
    <Box className={classes.systemAccountContainer}>
      <Typography className={classes.systemAccountsBlockLabel} mb="12px">
        System Accounts
      </Typography>

      <Box className={classes.systemAccountsInnerContainer}>
        {bankingData?.accounts && (
          <>
            <FirmBankingSystemAccountContainer
              setSelectedAccountType={setSelectedAccountType}
              setIsWithdrawalPopupOpen={setIsWithdrawalPopupOpen}
              accountType={'REV'}
              accountData={bankingData.accounts.rev}
            />
            <FirmBankingSystemAccountContainer
              setSelectedAccountType={setSelectedAccountType}
              setIsWithdrawalPopupOpen={setIsWithdrawalPopupOpen}
              accountType={'EXP'}
              accountData={bankingData.accounts.exp}
            />
            {/* <FirmBankingSystemAccountContainer
              setSelectedAccountType={setSelectedAccountType}
              setIsWithdrawalPopupOpen={setIsWithdrawalPopupOpen}
              accountType={'lien'}
              accountData={bankingData.accounts.lien}
            /> */}
            {/* <FirmBankingSystemAccountContainer
              setSelectedAccountType={setSelectedAccountType}
              setIsWithdrawalPopupOpen={setIsWithdrawalPopupOpen}
              accountType={'case'}
              accountData={bankingData.accounts.case}
            /> */}
          </>
        )}
      </Box>

      <PaginationProvider key="bankingUnrestrictedAccountsView">
        <FirmUnrestrictedAccountsView
          setShortUnrestrictedAccountData={setShortUnrestrictedAccountData}
        />
      </PaginationProvider>

      <PaginationProvider key="bankingTrustAccountsView">
        <FirmTrustAccountsView />
      </PaginationProvider>

      <FormProvider {...formMethods}>
        <FirmBankingWithdrawPopup
          isOpen={isWithdrawalPopupOpen}
          setSelectedAccountType={setSelectedAccountType}
          handleClosePopup={() => setIsWithdrawalPopupOpen(false)}
          selectedAccountType={selectedAccountType}
          isFormSubmitting={isFormSubmitting}
          // handleOpenVerificationPopup={() => setIsVerificationPopupOpen(true)}
          headlineText="Withdraw Funds"
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <FirmBankingWithdrawForm
            unrestrictedAccountOptionsData={shortUnrestrictedAccountData}
            selectedAccountType={selectedAccountType}
          />
        </FirmBankingWithdrawPopup>
      </FormProvider>

      {/* <VerificationPopup
        isOpen={isVerificationPopupOpen}
        // onSuccess={() => {
        //   setIsVerificationPopupOpen(false);
        //   handleSubmit(onSubmitForm)();
        // }}
        onSubmit={handleSubmitWithdraw}
        onCancel={handleCloseVerificationPopup}
      /> */}

      <VerificationPopup
        isOpen={!!withdrawFundsData}
        onSubmit={handleSubmitWithdraw}
        onCancel={() => setWithdrawFundsData(null)}
      />
    </Box>
  );
};

export default FirmBankingAccountsView;